import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getRestaurantPhotoUrl } from "@/lib/google";

// In-memory cache so the same restaurant doesn't refetch when it appears
// in multiple lists (e.g. nearby + search) during one session.
const memCache = new Map<string, string | null>();

type Props = {
  restaurantId: string;
  photoUrl: string | null | undefined;
  // Pass when known (saves a DB roundtrip). Pass undefined to allow the
  // component to look it up lazily from the restaurants row.
  googlePlaceId?: string | null;
  emoji: string;
  alt: string;
  imgClassName?: string;
};

export function RestaurantPhoto({
  restaurantId,
  photoUrl,
  googlePlaceId,
  emoji,
  alt,
  imgClassName = "w-full h-full object-cover",
}: Props) {
  const [fetched, setFetched] = useState<string | null>(
    memCache.get(restaurantId) ?? null,
  );
  const didFetch = useRef(false);

  useEffect(() => {
    if (photoUrl) return;
    if (fetched) return;
    if (didFetch.current) return;
    didFetch.current = true;

    let cancelled = false;
    (async () => {
      // Resolve google_place_id if caller didn't provide one.
      let placeId = googlePlaceId ?? null;
      if (placeId === undefined || placeId === null) {
        const { data } = await supabase
          .from("restaurants")
          .select("google_place_id")
          .eq("id", restaurantId)
          .maybeSingle();
        placeId = (data as any)?.google_place_id ?? null;
      }
      if (!placeId) {
        memCache.set(restaurantId, null);
        return;
      }
      const url = await getRestaurantPhotoUrl(placeId);
      if (cancelled) return;
      memCache.set(restaurantId, url);
      if (url) {
        setFetched(url);
        // Persist so subsequent loads never need to call Google.
        supabase
          .from("restaurants")
          .update({ photo_url: url })
          .eq("id", restaurantId)
          .then(({ error }) => {
            if (error) console.warn("[RestaurantPhoto] update failed", error);
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [restaurantId, photoUrl, googlePlaceId, fetched]);

  const effective = photoUrl || fetched;
  if (effective) {
    return <img src={effective} alt={alt} className={imgClassName} />;
  }
  return <span>{emoji}</span>;
}
